import { ReactElement } from 'react'
import { Box } from '@mui/material'
import {
  CopilotChat,
  type CopilotChatInput,
  useInterrupt,
  useRenderTool,
} from '@copilotkit/react-core/v2'
import '@copilotkit/react-core/v2/styles.css'
import { CHAT_LABELS, CHAT_MARKDOWN_COMPONENTS, decidedChangeOf, localAgentId } from '../utils'
import ChangeApprovalCard from './ChangeApprovalCard'
import ChatInput from './ChatInput'
import DecidedChangeSummary from './DecidedChangeSummary'
import HiddenControl from './HiddenControl'
import ReasoningContent from './ReasoningContent'

const MESSAGE_VIEW = {
  assistantMessage: {
    markdownRenderer: { components: CHAT_MARKDOWN_COMPONENTS },
    regenerateButton: HiddenControl,
  },
  reasoningMessage: { contentView: ReasoningContent },
  userMessage: { editButton: HiddenControl },
}

interface Props {
  // CopilotChat picks up the thread-scoped agent, registered once by the
  // widget content, by `localAgentId(threadId)`.
  threadId: string
}

/**
 * The chat itself: CopilotKit's prebuilt `CopilotChat` (message view with
 * markdown and tool-call rendering, composer, stop, suggestions) bound to
 * this thread's agent, and the approval card for changes the agent paused
 * for (AG-UI interrupts, rendered into the chat). Connecting brings the
 * thread's history and its open approval cards: the Copilot runtime serves
 * them from the API's stored thread (`copilot-runtime/src/runner.ts`).
 */
const ChatPanel = ({ threadId }: Props): ReactElement => {
  useInterrupt({
    agentId: localAgentId(threadId),
    render: ({ interrupts, resolve }) => (
      <ChangeApprovalCard interrupts={interrupts} resolve={resolve} />
    ),
  })

  // Every tool call goes through here: decided changes show what was decided
  // (also in a restored history), everything else stays hidden as before.
  useRenderTool(
    {
      name: '*',
      render: ({ name, parameters, result }) => {
        const change = decidedChangeOf(name, parameters, result)
        return change ? <DecidedChangeSummary change={change} /> : null
      },
    },
    [],
  )

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        '& > *': { flex: 1 },
        // CopilotKit sets messages in its `prose` style at 16px; the chat
        // uses the app's body text size (as its cards and field do).
        '& .cpk\\:prose': { fontSize: 14, lineHeight: 1.6 },
        // CopilotKit pads the thread's scroll content by 16px per side, on
        // top of the drawer's own padding; the narrow drawer needs the width.
        '& .cpk\\:overflow-y-auto > .cpk\\:px-4': { px: 0.5 },
      }}
      data-cy="ai-chat-messages"
    >
      <CopilotChat
        agentId={localAgentId(threadId)}
        threadId={threadId}
        labels={CHAT_LABELS}
        messageView={MESSAGE_VIEW}
        // The slot is typed as CopilotKit's own input with its static parts;
        // CopilotChat only renders it with the input's props.
        input={ChatInput as unknown as typeof CopilotChatInput}
      />
    </Box>
  )
}

export default ChatPanel
